import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedNzModule } from './shared-nz.module';
import { SubmitResultsComponent } from './components/submit-results/submit-results.component';
import { HomeComponent } from './pages/home/home.component';
import { MyDashboard } from './pages/my-dashboard/my-dashboard.component';
import { DashboardGridComponent } from './components/dashboard-grid/dashboard-grid.component';
import { CardDashboardComponent } from './components/card-dashboard/card-dashboard.component';
import { NgxEchartsModule } from 'ngx-echarts';
import { LottieComponent, provideLottieOptions   } from 'ngx-lottie';
import player from 'lottie-web';
import { CalAngularModule } from '@cvx/cal-angular';
import { environment } from 'src/environments/environment';
import { SubmitFailedComponent } from './components/submit-failed/submit-failed.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NextPage } from '@cvx/nextpage';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { UserManagementComponent } from './pages/user-management/user-management.component';
import { EditUserComponent } from './components/edit-user/edit-user.component';
import { ProposalTypesComponent } from './pages/proposal-types/proposal-types.component';
import { RequestGeneralDetailsComponent } from './components/request-general-details/request-general-details.component';
import { AcquisitionProposalDetailsComponent } from './components/acquisition-proposal-details/acquisitionproposal-details.component';
import { DissolutionProposalDetailsComponent } from './components/dissolution-proposal-details/dissolution-proposal-details.component';
import { AcquisitionProposalOverviewComponent } from './components/acquisition-proposal-overview/acquisition-proposal-overview.component';
import { DissolutionProposalOverviewComponent } from './components/dissolution-proposal-overview/dissolution-proposal-overview.component';
import { AcquisitionProposalComponent } from './pages/acquisition-proposal/acquisition-proposal.component';
import { DissolutionProposalComponent } from './pages/dissolution-proposal/dissolution-proposal.component';
import { MergerProposalComponent } from './pages/merger-proposal/merger-proposal.component';
import { AcquisitionEntitySelectionComponent } from './components/acquisition-entity-selection/acquisition-entity-selection.component';
import { DissolutionEntitySelectionComponent } from './components/dissolution-entity-selection/dissolution-entity-selection.component';
import { AcquisitionEntityQuestionsComponent } from './components/acquisition-entity-questions/acquisition-entity-questions.component';
import { MergerEntityDetailsComponent } from './components/merger-entity-details/merger-entity-details.component';
import { AcquisitionProposedSlateComponent } from './components/acquisition-proposed-slate/acquisition-proposed-slate.component';
import { AssignmentsComponent } from './components/assignments/assignments.component';
import { ProposalReviewComponent } from './components/proposal-review/proposal-review.component';
import { CanDeativateProposalTypeGuardService } from './core/services/CanDeactivateAcquisitionGuardService';
import { DataServiceProposalOverview } from './core/services/data_service_proposal_overview';
import { DataService } from './core/services/data_service';
import { DataServiceEntitySelection } from './core/services/data_service_entity_selection';
import { DataServiceGeneralDetails } from './core/services/data_service_general_details';
import { DataServiceContacts } from './core/services/data_service_contacts';
import { DataServiceEntityQuestions } from './core/services/data_service_entity_questions';
import { ViewRequestsComponent } from './pages/view-requests/view-requests.component';
import { ContactsComponent } from './components/contacts/contacts.component';
import { CommentsComponent } from './components/comments/comments.component';
import { DataServiceComments } from './core/services/data_service_comments';
import { PageLoaderComponent } from './pages/page-loader/page-loader.component';
import { ServerErrorComponent } from './components/server-error/server-error.component';
import { ConcurDeclineComponent } from './components/concur-decline/concur-decline.component';
import { IfcReviewerComponent } from './components/ifc-reviewer/ifc-reviewer.component';
import { NotesSubmissionComponent } from './components/notes-submission/notes-submission.component';
import { DataServiceApprovals } from './core/services/data_service_approvals';
import { DissolutionProposalAttachmentsComponent } from './components/dissolution-proposal-attachments/dissolution-proposal-attachments.component';
import { DissolutionProposalReviewComponent } from './components/dissolution-proposal-review/dissolution-proposal-review.component';
import { MergerProposalOverviewComponent } from './components/merger-proposal-overview/merger-proposal-overview.component';
import { MergerProposalDetailsComponent } from './components/merger-proposal-details/merger-proposal-details.component';
import { MergerProposalAttachmentsComponent } from './components/merger-proposal-attachments/merger-proposal-attachments.component';
import { MergerProposalReviewComponent } from './components/merger-proposal-review/merger-proposal-review.component';
import { SalesProposalComponent } from './pages/sales-proposal/sales-proposal.component';
import { SalesProposalDetailsComponent } from './components/sales-proposal-details/sales-proposal-details.component';
import { SalesEntitySelectionComponent } from './components/sales-entity-selection/sales-entity-selection.component';
import { SalesProposalReviewComponent } from './components/sales-proposal-review/sales-proposal-review.component';
import { FormationProposalComponent } from './pages/formation-proposal/formation-proposal.component';
import { FormationProposalDetailsComponent } from './components/formation-proposal-details/formation-proposal-details.component';
import { FormationProposalReviewComponent } from './components/formation-proposal-review/formation-proposal-review.component';
import { FormationEntitySelectionComponent } from './components/formation-entity-selection/formation-entity-selection.component';
import { FormationProposedSlateComponent } from './components/formation-proposal-slate/formation-proposal-slate.component';
import { FormationProposalOverviewComponent } from './components/formation-proposal-overview/formation-proposal-overview.component';
import { OwnershipRestructuringProposalDetailsComponent } from './components/ownership-restructuring-proposal-details/ownership-restructuring-proposal-details.component';
import { OwnershipRestructuringProposalOverviewComponent } from './components/ownership-restructuring-proposal-overview/ownership-restructuring-proposal-overview.component';
import { OwnershipRestructuringProposalComponent } from './pages/ownership-restructuring-proposal/ownership-restructuring-proposal.component';
import { OwnershipRestructuringProposalReviewComponent } from './components/ownership-restructuring-proposal-review/ownership-restructuring-proposal-review.component';
import { OwnershipRestructuringEntitySelectionComponent } from './components/ownership-restructuring-entity-selection/ownership-restructuring-entity-selection.component';
import { QualificationWithdrawalProposalComponent } from './pages/qualification-withdrawal-proposal/qualification-withdrawal-proposal.component';
import { QualificationWithdrawalProposalDetailsComponent } from './components/qualification-withdrawal-proposal-details/qualification-withdrawal-proposal-details.component';
import { QualificationWithdrawalEntitySelectionComponent } from './components/qualification-withdrawal-entity-selection/qualification-withdrawal-entity-selection.component';
import { QualificationWithdrawalProposalReviewComponent } from './components/qualification-withdrawal-proposal-review/qualification-withdrawal-proposal-review.component';
import { QualificationWithdrawalProposalOverviewComponent } from './components/qualification-withdrawal-proposal-overview/qualification-withdrawal-proposal-overview.component';
import { OthersProposalComponent } from './pages/others-proposal/others-proposal.component'
import { OthersProposalReviewComponent } from './components/others-proposal-review/others-proposal-review.component';
import { OthersProposalOverviewComponent } from './components/others-proposal-overview/others-proposal-overview.component';
import { OthersProposalDetailsComponent } from './components/others-proposal-details/others-proposal-details.component';
import { OthersEntitySelectionComponent } from './components/others-entity-selection/others-entity-selection.component';
import { LocalReviewerComponent } from './components/local-reviewer/local-reviewer.component';

@NgModule({ declarations: [
        AppComponent,
        SubmitResultsComponent,
        HomeComponent,
        MyDashboard,
        DashboardGridComponent,
        CardDashboardComponent,
        SubmitFailedComponent,
        FileUploadComponent,
        PageHeaderComponent,
        UserManagementComponent,
        EditUserComponent,
        ProposalTypesComponent,
        RequestGeneralDetailsComponent,
        DissolutionProposalDetailsComponent,
        AcquisitionProposalComponent,
        MergerEntityDetailsComponent,
        MergerProposalComponent,
        MergerProposalOverviewComponent,
        MergerProposalDetailsComponent,
        MergerProposalAttachmentsComponent,
        MergerProposalReviewComponent,
        AcquisitionProposalDetailsComponent,
        AcquisitionProposalOverviewComponent,
        DissolutionProposalOverviewComponent,
        AcquisitionEntitySelectionComponent,
        DissolutionEntitySelectionComponent,
        AcquisitionEntityQuestionsComponent,
        DissolutionProposalAttachmentsComponent,
        AssignmentsComponent,
        ContactsComponent,
        CommentsComponent,
        AcquisitionProposedSlateComponent,
        ProposalReviewComponent,
        DissolutionProposalReviewComponent,
        DissolutionProposalComponent,
        ViewRequestsComponent,
        ServerErrorComponent,
        PageLoaderComponent,
        ConcurDeclineComponent,
        IfcReviewerComponent,
        NotesSubmissionComponent,
        DateAgoPipe,
        SalesProposalComponent,
        SalesProposalDetailsComponent,
        SalesEntitySelectionComponent,
        SalesProposalReviewComponent,
        FormationProposalComponent,
        FormationProposalDetailsComponent,
        FormationProposalOverviewComponent,
        FormationProposalReviewComponent,
        FormationEntitySelectionComponent,
        FormationProposedSlateComponent,
        OwnershipRestructuringProposalComponent,
        OwnershipRestructuringProposalOverviewComponent,
        OwnershipRestructuringProposalDetailsComponent,
        OwnershipRestructuringProposalReviewComponent,
        OwnershipRestructuringEntitySelectionComponent,
        QualificationWithdrawalProposalComponent,
        QualificationWithdrawalProposalDetailsComponent,
        QualificationWithdrawalEntitySelectionComponent,
        QualificationWithdrawalProposalReviewComponent,
        QualificationWithdrawalProposalOverviewComponent,
        OthersProposalComponent,
        OthersProposalReviewComponent,
        OthersProposalOverviewComponent,
        OthersProposalDetailsComponent,
        OthersEntitySelectionComponent,
        LocalReviewerComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        NextPage,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        SharedNzModule,
        ScrollingModule,
        DragDropModule,
        LottieComponent,
        CalAngularModule.forRoot(environment.calConfig),
        //  LottieModule.forRoot({ player: playerFactory }),
        NgxEchartsModule.forRoot({
            echarts: () => import('echarts')
        })], providers: [
            provideLottieOptions({
      player: () => import('lottie-web'),
    }),
        { provide: NZ_I18N, useValue: en_US },
        NzMessageService, CanDeativateProposalTypeGuardService,
        DataService, DataServiceGeneralDetails,
        DataServiceEntitySelection, DataServiceEntityQuestions,
        DataServiceProposalOverview, DataServiceContacts, DataServiceComments,
        DataServiceApprovals,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
// export function playerFactory() {
//   return player;
//}
